/**
 * Simulator layout interactions: leave modal + sidebar mobile swipe.
 * Mirrors Simulator.vue + Sidebar.vue behaviour.
 */
import { openModal } from "@/shared/modal";

/**
 * Simulator.vue leave confirmation: Modal.vue (partials/modal) opened by the header button;
 * the overlay, the close button and « Continuer ma demande » close it, Escape does not.
 * « Quitter » reloads the vehicle page (Simulator.vue leave(): full reload).
 */
function initLeaveModal(): void {
    const template = document.querySelector<HTMLTemplateElement>('template[data-modal-template="simulation-leave"]');
    const leaveBtn = document.querySelector<HTMLButtonElement>('[data-testid="leave-btn"]');
    if (!template || !leaveBtn) return;

    const simulator = document.querySelector<HTMLElement>('.simulator');
    const leaveUrl = simulator?.dataset.leaveUrl ?? '/';
    // Remerciement sur mobile : la réservation est faite, il n'y a plus rien à interrompre
    const leaveDirect = simulator?.dataset.leaveDirect === '1';

    leaveBtn.addEventListener('click', () => {
        if (leaveDirect && window.matchMedia('(max-width: 767.98px)').matches) {
            window.location.href = leaveUrl;
            return;
        }
        const modal = openModal(template);
        modal.querySelector('[data-attr="simulation-leave-confirm"]')?.addEventListener('click', () => {
            window.location.href = leaveUrl;
        });
    });
}

/**
 * Sidebar.vue: the bar opens (swipe up, click) and closes (swipe down, click) the sidebar on
 * mobile, for a signed-in visitor only (allowSwipe); `-active` on the sidebar and
 * `responsive-footer` on its footer follow isOpen (open at first for a signed-in visitor).
 * setSidebarOpen() is Simulator.vue's forceOpenSidebar (Sidebar.vue forceOpen), used by the résumé.
 *
 * Mobile : le volet (« Ajuster ») suit le rythme commun des volets (components/_sheet.scss) : il
 * monte depuis le bandeau en 280 ms, sans délai, la calculette et le fond en fondu de 200 ms ; il
 * redescend en 200 ms avant de reprendre sa taille de bandeau. Réduction des animations : fondus
 * seuls, sans mouvement.
 */
const SHEET_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'; // $ease-sheet de components/_sheet.scss
const OPEN_MS = 280;
const CLOSE_MS = 200;
const FADE_MS = 200;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let running: Animation[] = [];

function applySidebarOpen(sidebar: HTMLElement, open: boolean): void {
    sidebar.classList.toggle('-active', open);
    sidebar.querySelector<HTMLElement>('.simulator__sidebar__footer > div')?.classList.toggle('responsive-footer', open);
    syncSidebarInert();
}

/** Ouvert, ou en train de s'ouvrir ; pas en train de se refermer (-closing). */
function isSidebarOpen(sidebar: HTMLElement): boolean {
    return sidebar.classList.contains('-active') && !sidebar.classList.contains('-closing');
}

/** Fond assombri sous le volet, créé au premier mouvement (mobile seulement, _layout.scss). */
function sidebarVeil(sidebar: HTMLElement): HTMLElement {
    let veil = document.querySelector<HTMLElement>('.simulator__sidebar-veil');
    if (!veil) {
        veil = document.createElement('div');
        veil.className = 'simulator__sidebar-veil';
        veil.setAttribute('aria-hidden', 'true');
        sidebar.before(veil);
    }
    return veil;
}

export function setSidebarOpen(open: boolean): void {
    const sidebar = document.getElementById('simulator-sidebar');
    if (!sidebar) return;
    // Un mouvement en cours est arrêté là où il est : le suivant repart de cette position et de ces
    // opacités (rouvert pendant la fermeture : la calculette ne clignote pas)
    const visualTop = sidebar.getBoundingClientRect().top;
    const content = sidebar.querySelector<HTMLElement>('.simulator__sidebar__content');
    const veil = mobileQuery.matches ? sidebarVeil(sidebar) : null;
    const moving = running.some((animation) => animation.playState === 'running');
    const opacityOf = (el: HTMLElement | null, idle: number): number =>
        moving && el ? Number(getComputedStyle(el).opacity) : idle;
    const wasOpen = isSidebarOpen(sidebar);
    const contentFrom = opacityOf(content, open ? 0 : 1);
    const veilFrom = opacityOf(veil, open ? 0 : 1);
    running.forEach((animation) => animation.cancel());
    running = [];
    sidebar.classList.remove('-closing');
    if (wasOpen === open || !veil || typeof sidebar.animate !== 'function') {
        applySidebarOpen(sidebar, open);
        return;
    }
    const still = reducedMotion.matches;

    if (open) {
        applySidebarOpen(sidebar, true);
        const from = visualTop - sidebar.getBoundingClientRect().top;
        running = [
            ...(still ? [] : [sidebar.animate([{ transform: `translateY(${from}px)` }, { transform: 'translateY(0)' }], { duration: OPEN_MS, easing: SHEET_EASE })]),
            ...(content ? [content.animate([{ opacity: contentFrom }, { opacity: 1 }], { duration: FADE_MS, easing: 'ease-out' })] : []),
            veil.animate([{ opacity: veilFrom }, { opacity: 1 }], { duration: FADE_MS, easing: 'ease-out' }),
        ];
        return;
    }

    // Fermeture : position du bandeau mesurée volet replié, puis retour à l'état ouvert pour l'animer
    applySidebarOpen(sidebar, false);
    const closedTop = sidebar.getBoundingClientRect().top;
    applySidebarOpen(sidebar, true);
    const openTop = sidebar.getBoundingClientRect().top;
    const options: KeyframeAnimationOptions = { duration: CLOSE_MS, easing: SHEET_EASE, fill: 'forwards' };
    const animations = [
        ...(still ? [] : [sidebar.animate([{ transform: `translateY(${visualTop - openTop}px)` }, { transform: `translateY(${closedTop - openTop}px)` }], options)]),
        ...(content ? [content.animate([{ opacity: contentFrom }, { opacity: 0 }], { ...options, easing: 'ease-out' })] : []),
        veil.animate([{ opacity: veilFrom }, { opacity: 0 }], { ...options, easing: 'ease-out' }),
    ];
    running = animations;
    sidebar.classList.add('-closing');
    // Le volet reprend sa taille de bandeau à la dernière image du mouvement
    void Promise.all(animations.map((animation) => animation.finished)).then(
        () => {
            if (running !== animations) return;
            sidebar.classList.remove('-closing');
            applySidebarOpen(sidebar, false);
            animations.forEach((animation) => animation.cancel());
            running = [];
        },
        () => undefined,
    );
}

const mobileQuery = window.matchMedia('(max-width: 767.98px)');

/**
 * Mobile : le volet replié cache la calculette (opacité, hauteur nulle) ; ses champs sortent aussi
 * du parcours clavier. Sur ordinateur, la colonne est toujours visible.
 */
function syncSidebarInert(): void {
    const sidebar = document.getElementById('simulator-sidebar');
    const content = sidebar?.querySelector<HTMLElement>('.simulator__sidebar__content');
    if (!sidebar || !content) return;
    content.inert = mobileQuery.matches && !sidebar.classList.contains('-active');
}

function initSidebarSwipe(): void {
    const sidebar = document.getElementById('simulator-sidebar');
    const container = document.getElementById('sidebar-bar-container');
    if (!sidebar || !container || sidebar.dataset.swipe !== '1') return;

    const setOpen = setSidebarOpen;

    // useSwipe: minDiffToActive 15
    let touchStartY = 0;
    container.addEventListener('touchstart', (e: TouchEvent) => {
        touchStartY = e.touches[0].clientY;
    }, { passive: true });
    container.addEventListener('touchend', (e: TouchEvent) => {
        const deltaY = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(deltaY) < 15) return;
        setOpen(deltaY < 0);
    }, { passive: true });

    container.addEventListener('click', () => {
        setOpen(!isSidebarOpen(sidebar));
    });
}

/** Passage mobile ↔ ordinateur pendant un mouvement du volet : il s'arrête dans son état final. */
function settleSidebar(): void {
    const sidebar = document.getElementById('simulator-sidebar');
    if (sidebar && running.length) {
        const open = isSidebarOpen(sidebar);
        running.forEach((animation) => animation.cancel());
        running = [];
        sidebar.classList.remove('-closing');
        applySidebarOpen(sidebar, open);
    }
    syncSidebarInert();
}

function init(): void {
    initLeaveModal();
    initSidebarSwipe();
    syncSidebarInert();
    mobileQuery.addEventListener('change', settleSidebar);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
